import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Decorator, Input, Tooltip } from '@myhealth-raccoon/components';
import { ControlledSelect as Select, validation } from '@myhealth-raccoon/forms';
import { HelpCircle, MarkerPin01 } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';

import { AppointmentType } from 'src/services/availability'
import DoctorIcon from 'src/assets/doctor.svg?react';
import { CheckboxItem } from 'src/components/CheckboxItem';
import { UserWithSiteModel } from 'src/services/user';
import { filterToStandardAppointmentTypes, isFaceToFace, newPatientRegex } from 'src/utils/appointmentTypes';
import { bookingService, BookingUserProfileModel } from 'src/services/booking';
import { availabilityService } from 'src/services/availability';

export type DoctorBookingInformationForm = {
    patientType: "existing" | "new",
    appointmentMode: "faceToFace" | "telehealth",
    appointmentTypeId: string,
    siteId: number,
    doctorId: number,
}

type Props = {
    className?: string;
    clinics: UserWithSiteModel[],
    onNewValues?(values: DoctorBookingInformationForm): void;
    onValidate?(valid: boolean): void;
    defaultValues?: Partial<DoctorBookingInformationForm>;
    onDoctorDetails?(doctor: BookingUserProfileModel): void;
}

function filterAppointmentTypes(appointmentTypes: AppointmentType[],
    doctorDetails: BookingUserProfileModel,
    patientType: "existing" | "new",
    appointmentMode: "faceToFace" | "telehealth",
) {
    return appointmentTypes.filter((apt) =>
        (isFaceToFace(apt) === (appointmentMode === "faceToFace"))
        && (apt.newPatient || (patientType === "existing"))
        && (doctorDetails.apptTypeIds.includes(Number(apt.appointmentTypeId)))
        && (patientType === "existing" || !doctorDetails.noNewPatient)
        && ((patientType !== "existing") || !apt.appointmentName.match(newPatientRegex))
    )
}

const emptyArray: unknown[] = [];
export function BookWithDoctorForm({
    className,
    clinics = emptyArray as UserWithSiteModel[],
    onNewValues,
    onValidate,
    defaultValues,
    onDoctorDetails
}: Props) {
    const [doctorDetailsAtSite, setDoctorDetailsAtSite] = useState<BookingUserProfileModel>();
    const [appointmentTypesAtSite, setAppointmentTypesAtSite] = useState<AppointmentType[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [_requestError, setRequestError] = useState<unknown>();

    useEffect(() => {
        if (doctorDetailsAtSite) {
            onDoctorDetails?.(doctorDetailsAtSite)
        }
    }, [onDoctorDetails, doctorDetailsAtSite])

    const {
        handleSubmit: innerSubmit,
        register,
        control,
        formState: { errors, dirtyFields },
        trigger,
        watch,
        setValue
    } = useForm<DoctorBookingInformationForm>({
        defaultValues,
        shouldFocusError: false,
    });

    const patientType = watch('patientType');
    const appointmentMode = watch('appointmentMode');
    const appointmentTypeId = watch('appointmentTypeId');
    const siteId = watch('siteId');

    const site = useMemo(() =>
        clinics.find(c => c.siteId === siteId),
        [clinics, siteId]);

    const filteredAppointmentTypes = useMemo(() => {
        if (appointmentTypesAtSite && doctorDetailsAtSite) {
            return filterAppointmentTypes(appointmentTypesAtSite, doctorDetailsAtSite, patientType, appointmentMode)
        }
        return [];
    }, [appointmentMode, appointmentTypesAtSite, doctorDetailsAtSite, patientType])

    useEffect(() => {
        if (site) {
            setIsLoading(true);
            (async () => {
                try {
                    const [doc, appts] = await Promise.all([
                        bookingService.GetDoctorDetails(site?.siteId, site?.erpUserId),
                        availabilityService.GetAppointmentTypes(site?.siteId)
                    ])
                    setDoctorDetailsAtSite(doc);
                    setAppointmentTypesAtSite(filterToStandardAppointmentTypes(appts));
                    setIsLoading(false);
                } catch (e) {
                    setRequestError(e);
                    setDoctorDetailsAtSite(undefined);
                    setAppointmentTypesAtSite(undefined);
                    setIsLoading(false);
                }
            })();
        }
    }, [site]);

    const selectedAppointmentType = useMemo(
        () => filteredAppointmentTypes?.find(a => `${a.appointmentTypeId}` === `${appointmentTypeId}`),
        [appointmentTypeId, filteredAppointmentTypes])

    useEffect(() => {
        if (onNewValues) {
            innerSubmit(onNewValues)();
            const { unsubscribe } = watch(() => innerSubmit(onNewValues)());
            return unsubscribe;
        }
    }, [innerSubmit, onNewValues, watch]);

    useEffect(() => {
        trigger('appointmentTypeId');
    }, [patientType, appointmentMode, siteId, appointmentTypeId, trigger]);

    useEffect(() => {
        // if the currently selected appointment type doesn't match filters
        if (!selectedAppointmentType && filteredAppointmentTypes) {
            setValue("appointmentTypeId", filteredAppointmentTypes.length === 1 ? filteredAppointmentTypes[0].appointmentTypeId : null!);
        }
    }, [selectedAppointmentType, filteredAppointmentTypes, setValue]);

    useEffect(() => {
        if (site) {
            // whenever site changes, set doctorId with it
            setValue("doctorId", site.erpUserId);
        }
    }, [setValue, site]);

    register("appointmentTypeId", { validate: validation.required("Please select an appointment type") });
    register("siteId", { validate: validation.required("Please select a clinic") });

    useLayoutEffect(() => {
        onValidate?.(!!site && !!selectedAppointmentType);
    }, [site, selectedAppointmentType, onValidate]);

    return <form className={classNames('space-y-4', className)}>
        <Decorator
            fieldset
            fieldError={errors.patientType}
        >
            <div className="flex flex-row gap-3">
                <CheckboxItem
                    {...register("patientType")}
                    type='radio'
                    id="patientType_existing"
                    value="existing"
                >
                    Existing patient
                </CheckboxItem>
                <CheckboxItem
                    {...register("patientType")}
                    type='radio'
                    id="patientType_new"
                    value="new"
                >
                    <span className="hidden sm:flex">New patient at this centre</span>
                    <span className='sm:hidden'>New patient</span>
                </CheckboxItem>
            </div>
        </Decorator>
        <Decorator
            fieldset
            fieldError={errors.appointmentMode}
        >
            <div className="flex flex-row gap-3">
                <CheckboxItem
                    {...register("appointmentMode")}
                    type='radio'
                    id="appointmentMode_faceToFace"
                    value="faceToFace"
                >
                    Face to Face
                </CheckboxItem>
                <CheckboxItem
                    {...register("appointmentMode")}
                    type='radio'
                    id="appointmentMode_telehealth"
                    value="telehealth"
                >
                    Telehealth{' '}
                    <Tooltip
                        tooltipContent="Telehealth allows you to consult a healthcare provider by phone or a video call."
                        attachmentPoint='top'
                        className='inline-block relative top-0.5'
                    >
                        <HelpCircle className='w-4 h-4 text-gray-400' />
                    </Tooltip>
                </CheckboxItem>
            </div>
        </Decorator>
        {filteredAppointmentTypes.length > 1
            ? <Select
                disabled={isLoading}
                control={control}
                rules={{ validate: validation.required("Please select an appointment type") }}
                name='appointmentTypeId'
                id='appointmentType'
                fieldError={dirtyFields.appointmentTypeId ? errors.appointmentTypeId : undefined}
                options={filteredAppointmentTypes}
                labelSelector={apt => apt.appointmentName.replace(newPatientRegex, "")}
                keyProperty='appointmentTypeId'
                valueProperty='appointmentTypeId'
                icon={DoctorIcon}
                placeholder='Select an appointment'
                hint={selectedAppointmentType && !isFaceToFace(selectedAppointmentType) && "Please note that to access GP Telehealth services, you must have had an face-to-face consultation within the past 12 months."}

            />
            : <Input
                disabled
                value={selectedAppointmentType?.appointmentName}
                placeholder="Select an appointment"
                fieldError={errors.appointmentTypeId}
                errorMessage={filteredAppointmentTypes.length === 0 ? "There are no available appointment types for your selection." : undefined}
                error={filteredAppointmentTypes.length === 0}
                icon={DoctorIcon}
            />}
        {clinics.length > 1 && !isLoading
            ? <Select
                control={control}
                name='siteId'
                id='clinic'
                fieldError={dirtyFields.siteId ? errors.siteId : undefined}
                options={clinics}
                labelProperty='siteName'
                keyProperty='siteId'
                valueProperty='siteId'
                icon={MarkerPin01}
                placeholder='Select a clinic'
            />
            : <Input
                disabled
                value={site?.siteName}
                placeholder='Select a clinic'
                fieldError={errors.siteId}
                errorMessage={clinics.length === 0 ? "There are no providers currently accepting new patients for this appointment type." : undefined}
                error={clinics.length === 0}
                icon={MarkerPin01}
            />}
    </form>

}